import type { PostPreview } from '@/types';
import React, { FC, useRef } from 'react';
import { Link } from './link';
import { Image } from './image';
import { useInView } from '@/hooks/reveal-in-view';

export const PostCard: FC<{
  className?: string;
  card: PostPreview;
  readMore: string;
  theme?: 'light' | 'dark';
  style?: React.CSSProperties;
  /** Set for above-the-fold cards so their image loads eagerly (LCP). */
  imagePriority?: boolean;
}> = ({ className, card, readMore, theme, style, imagePriority }) => {
  const formatDate = (date: string) => {
    const dateObj = new Date(date);
    const month = dateObj.toLocaleString('default', { month: 'long' });
    const day = dateObj.getDate();

    return `${month} ${day}`;
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <Link
      href={`/resources/${card.group}/${card.slug}`}
      noStyle
      className={`postCard ${className ? className : ''} postCard_${
        theme || 'light'
      }`}
      style={style}
    >
      <div className="postCard--imageWrapper" ref={ref}>
        <Image
          src={card.coverImage}
          alt="Image"
          width={355}
          className="postCard--image"
          animateInView
          sizes="(min-width: 1024px) 40vw, 92vw"
          loading={imagePriority ? "eager" : "lazy"}
          fetchPriority={imagePriority ? "high" : undefined}
        />
      </div>
      <div className="postCard--content">
        <div
          className="postCard--details"
          style={{
            transform: isInView ? 'none' : `translateY(20px)`,
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
        >
          {card.label && <div className="postCard--tag">{card.label}</div>}
          <div className="postCard--date">{formatDate(card.date)}</div>
        </div>
        <h3
          className="postCard--title"
          style={{
            transform: isInView ? 'none' : `translateY(20px)`,
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
        >
          {card.title}
        </h3>
        <p
          className="postCard--description"
          style={{
            transform: isInView ? 'none' : `translateY(20px)`,
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
        >
          {card.description}
        </p>
        <div
          className="postCard--readMore"
          style={{
            transform: isInView ? 'none' : `translateY(20px)`,
            opacity: isInView ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
          }}
        >
          {readMore}
        </div>
      </div>
    </Link>
  );
};
